import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import { TabButton } from '@/components/ui/tab-button';
import {
  ArrowLeft,
  Copy,
  Check,
  Pause,
  Play,
  ExternalLink,
  AlertTriangle,
} from 'lucide-react';
import { adminApi, AdminSubscription } from '../services/adminApi';
import './AdminDashboard.css';

type DetailTab = 'overview' | 'usage' | 'notes';

// Map the status color names still returned by the get*Badge helpers below onto shadcn Badge
// classes, preserving the status color SEMANTICS after the migration (mirrors OrgBillingTab):
// success = emerald, warning = amber, danger = red, informative = neutral gray.
const BADGE_CLASS: Record<string, string> = {
  success: 'border-transparent bg-gold/15 text-foreground',
  warning: 'border-transparent bg-warning/10 text-warning',
  danger: 'border-transparent bg-destructive/10 text-destructive',
  brand: 'border-transparent bg-secondary text-foreground',
  informative: 'border-transparent bg-muted text-text-secondary',
  subtle: 'border-transparent bg-muted text-muted-foreground',
};

function badgeClass(color: string): string {
  return BADGE_CLASS[color] || BADGE_CLASS.informative;
}

function CopyableId({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <span className="copyable-id" title={value}>
      {label && <span className="copyable-id-label">{label}: </span>}
      <code>{value.length > 24 ? value.slice(0, 8) + '...' + value.slice(-8) : value}</code>
      <button className="copy-btn" onClick={handleCopy} title="Copy to clipboard">
        {copied ? <Check /> : <Copy />}
      </button>
    </span>
  );
}

function SubscriptionDetailPage() {
  const { subId } = useParams<{ subId: string }>();
  const [sub, setSub] = useState<AdminSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');

  // Notes editing
  const [editingNotes, setEditingNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesMessage, setNotesMessage] = useState<string | null>(null);

  // Suspend state
  const [showSuspend, setShowSuspend] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');

  const fetchSubscription = useCallback(async () => {
    if (!subId) return;
    try {
      setLoading(true);
      const data = await adminApi.getSubscription(subId);
      setSub(data);
      setEditingNotes(data.adminNotes || '');
      setError(null);
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
      setError('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  }, [subId]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const handleSaveNotes = async () => {
    if (!subId) return;
    setNotesSaving(true);
    setNotesMessage(null);
    try {
      await adminApi.updateNotes(subId, editingNotes);
      setNotesMessage('Notes saved');
      setTimeout(() => setNotesMessage(null), 3000);
      fetchSubscription();
    } catch (err) {
      console.error('Failed to save notes:', err);
      setNotesMessage('Failed to save');
    } finally {
      setNotesSaving(false);
    }
  };

  const handleSuspend = async () => {
    if (!subId || !suspendReason) return;
    try {
      await adminApi.suspendSubscription(subId, suspendReason);
      setShowSuspend(false);
      setSuspendReason('');
      fetchSubscription();
    } catch (err) {
      console.error('Failed to suspend:', err);
    }
  };

  const handleReinstate = async () => {
    if (!subId) return;
    try {
      await adminApi.reinstateSubscription(subId);
      fetchSubscription();
    } catch (err) {
      console.error('Failed to reinstate:', err);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, 'success' | 'warning' | 'danger' | 'informative'> = {
      Subscribed: 'success', PendingFulfillmentStart: 'warning',
      Suspended: 'danger', Unsubscribed: 'informative',
    };
    return <Badge variant="outline" className={badgeClass(colors[status] || 'informative')}>{status}</Badge>;
  };

  const handleBack = () => { window.location.href = '/admin'; };


  if (loading) {
    return (
      <div className="org-detail-page">
        <div className="admin-loading"><Spinner size={32} label="Loading subscription..." /></div>
      </div>
    );
  }

  if (error || !sub) {
    return (
      <div className="org-detail-page">
        <Button variant="ghost" onClick={handleBack}><ArrowLeft />Back</Button>
        <div className="admin-error"><AlertTriangle /><p>{error || 'Not found'}</p></div>
      </div>
    );
  }

  return (
    <div className="org-detail-page">
      <Button variant="ghost" onClick={handleBack} style={{ marginBottom: '1rem' }}>
        <ArrowLeft />
        Back to Subscriptions
      </Button>

      {/* Header Card */}
      <Card className="org-header-card">
        <div className="org-header-top">
          <div className="org-header-title">
            <h1>{sub.planId}</h1>
            <div className="org-header-meta">
              {getStatusBadge(sub.status)}
              <CopyableId value={sub.id} />
            </div>
          </div>
          <div className="org-header-actions">
            {sub.status === 'Subscribed' && (
              <Button variant="ghost" onClick={() => setShowSuspend(true)}>
                <Pause />
                Suspend
              </Button>
            )}
            {sub.status === 'Suspended' && (
              <Button onClick={handleReinstate}>
                <Play />
                Reinstate
              </Button>
            )}
            {sub.organizationId && (
              <Button
                variant="ghost"
                onClick={() => { window.location.href = `/admin/orgs/${sub.organizationId}`; }}
              >
                <ExternalLink />
                View Org
              </Button>
            )}
          </div>
        </div>

        <div className="org-header-stats">
          <div className="stat-item">
            <span className="stat-label">Seats</span>
            <span className="stat-value">{sub.quantity}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">MRR</span>
            <span className="stat-value">{formatCurrency(sub.monthlyRevenue)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Tokens This Period</span>
            <span className="stat-value">{sub.tokensReportedThisPeriod.toLocaleString()}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Created</span>
            <span className="stat-value">{formatDate(sub.createdAt)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Activated</span>
            <span className="stat-value">{formatDate(sub.activatedAt)}</span>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="org-tabs flex flex-wrap gap-2 pb-3">
        <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
          Overview
        </TabButton>
        <TabButton active={activeTab === 'usage'} onClick={() => setActiveTab('usage')}>
          Usage
        </TabButton>
        <TabButton active={activeTab === 'notes'} onClick={() => setActiveTab('notes')}>
          Notes
        </TabButton>
      </div>

      <div className="org-tab-content">
        {/* OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <Card className="info-section">
              <h3>Subscription Details</h3>
              <div className="info-grid">
                <div className="info-row">
                  <span className="info-label">Offer</span>
                  <span className="info-value">{sub.offerId}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Plan</span>
                  <span className="info-value">{sub.planId}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Organization</span>
                  <span className="info-value">
                    {sub.organizationId ? (
                      <a
                        href={`/admin/orgs/${sub.organizationId}`}
                        style={{ color: 'hsl(var(--accent-text))' }}
                      >
                        {sub.organizationId}
                      </a>
                    ) : 'Not activated'}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Marketplace Sub ID</span>
                  <span className="info-value">
                    <CopyableId value={sub.marketplaceSubscriptionId} />
                  </span>
                </div>
                {sub.employeeTemplateId && (
                  <div className="info-row">
                    <span className="info-label">Employee Template</span>
                    <span className="info-value"><code>{sub.employeeTemplateId}</code></span>
                  </div>
                )}
                <div className="info-row">
                  <span className="info-label">Auto-Renew</span>
                  <span className="info-value">
                    <Badge variant="outline" className={badgeClass(sub.autoRenew ? 'success' : 'informative')}>
                      {sub.autoRenew ? 'Yes' : 'No'}
                    </Badge>
                  </span>
                </div>
              </div>
            </Card>

            <Card className="info-section">
              <h3>People</h3>
              <div className="info-grid">
                <div className="info-row">
                  <span className="info-label">Beneficiary Email</span>
                  <span className="info-value" title={sub.beneficiaryEmail || '-'}>
                    {sub.beneficiaryEmail || '-'}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Beneficiary Tenant</span>
                  <span className="info-value">
                    {sub.beneficiaryTenantId ? <CopyableId value={sub.beneficiaryTenantId} /> : '-'}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Purchaser Email</span>
                  <span className="info-value" title={sub.purchaserEmail || '-'}>
                    {sub.purchaserEmail || '-'}
                  </span>
                </div>
              </div>
            </Card>

            <Card className="info-section">
              <h3>Timeline</h3>
              <div className="info-grid">
                <div className="info-row">
                  <span className="info-label">Created</span>
                  <span className="info-value">{formatDateTime(sub.createdAt)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Activated</span>
                  <span className="info-value">{formatDateTime(sub.activatedAt)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Term Start</span>
                  <span className="info-value">{formatDate(sub.termStartDate)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Term End</span>
                  <span className="info-value">{formatDate(sub.termEndDate)}</span>
                </div>
                {sub.suspendedAt && (
                  <div className="info-row">
                    <span className="info-label">Suspended</span>
                    <span className="info-value">{formatDateTime(sub.suspendedAt)}</span>
                  </div>
                )}
                {sub.suspensionReason && (
                  <div className="info-row">
                    <span className="info-label">Suspension Reason</span>
                    <span className="info-value" style={{ color: '#d13438' }}>{sub.suspensionReason}</span>
                  </div>
                )}
                {sub.cancelledAt && (
                  <div className="info-row">
                    <span className="info-label">Cancelled</span>
                    <span className="info-value">{formatDateTime(sub.cancelledAt)}</span>
                  </div>
                )}
                <div className="info-row">
                  <span className="info-label">Last Updated</span>
                  <span className="info-value">{formatDateTime(sub.updatedAt)}</span>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* USAGE */}
        {activeTab === 'usage' && (
          <div className="usage-tab">
            <Card className="info-section">
              <h3>Usage This Period</h3>
              <div className="info-grid">
                <div className="info-row">
                  <span className="info-label">Tokens Reported</span>
                  <span className="info-value">{sub.tokensReportedThisPeriod.toLocaleString()}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Last Metering Event</span>
                  <span className="info-value">{formatDateTime(sub.lastMeteringEventTime)}</span>
                </div>
              </div>
              <p style={{ color: '#666', marginTop: '1rem', fontSize: '0.85rem' }}>
                Detailed usage history will be available when usage tracking is fully implemented (Phase 2).
              </p>
            </Card>
          </div>
        )}

        {/* NOTES */}
        {activeTab === 'notes' && (
          <div className="notes-tab">
            <Card className="settings-form">
              <h3>Admin Notes</h3>
              <Textarea
                value={editingNotes}
                onChange={(e) => setEditingNotes(e.target.value)}
                placeholder="Internal notes about this subscription..."
                rows={6}
                style={{ width: '100%', marginTop: '0.75rem' }}
              />
              <div className="settings-actions">
                <Button onClick={handleSaveNotes} disabled={notesSaving}>
                  {notesSaving ? 'Saving...' : 'Save Notes'}
                </Button>
                {notesMessage && (
                  <p style={{ color: notesMessage.includes('Failed') ? '#d13438' : '#107c10' }}>
                    {notesMessage}
                  </p>
                )}
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Suspend overlay */}
      {showSuspend && (
        <div className="suspend-overlay">
          <Card className="suspend-card">
            <h3>Suspend Subscription</h3>
            <p>The customer's AIEs will be disabled.</p>
            <Textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Reason for suspension..."
              rows={3}
              style={{ width: '100%', marginTop: '0.75rem' }}
            />
            <div className="suspend-actions">
              <Button variant="outline" onClick={() => setShowSuspend(false)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={handleSuspend}
                disabled={!suspendReason}
              >
                Suspend
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export default SubscriptionDetailPage;
